import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link,
  Divider,
  Chip,
  Grid,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  Person,
  Email,
  Lock,
  Phone,
  AccountCircle,
} from '@mui/icons-material';
import registrationService, { RegistrationRequest, UsernameAvailability, EmailAvailability } from '../services/registrationService';
import debounce from 'lodash/debounce';
import {
  getStoredReferral,
  clearStoredReferral,
  captureReferralFromUrl,
} from '@shared/referralAttribution';

const steps = ['Account Info', 'Personal Details', 'Review & Confirm'];

function resolveReferralFromContext(searchParams: URLSearchParams) {
  captureReferralFromUrl();
  const urlReferralCode =
    searchParams.get('ref') || searchParams.get('RefID') || searchParams.get('refid') || '';
  const hasUrlReferral = urlReferralCode.length > 0;
  const stored = getStoredReferral();
  const hasCookieReferral = !hasUrlReferral && (stored?.ref?.length ?? 0) > 0;
  const effectiveRef = urlReferralCode || stored?.ref || '';
  const hasAppliedReferral = hasUrlReferral || hasCookieReferral;

  let effectiveSource: string;
  if (hasUrlReferral) {
    effectiveSource = searchParams.get('source') || 'UserReferral';
  } else if (hasCookieReferral) {
    effectiveSource = stored?.utm_campaign
      ? `DisplayAd:${stored.utm_campaign}`
      : stored?.landing || 'DisplayAd';
  } else {
    effectiveSource = searchParams.get('source') || (typeof window !== 'undefined' ? window.location.href : 'Registration');
  }

  return { urlReferralCode, hasUrlReferral, hasCookieReferral, hasAppliedReferral, effectiveRef, effectiveSource };
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFramed = typeof window !== 'undefined' && window.self !== window.top;
  const isHashMailboxHost =
    typeof window !== 'undefined' &&
    ['mail.qixit.com', 'stmail.qixit.com'].includes(window.location.hostname);
  const mailboxLoginUrl =
    typeof window !== 'undefined' && window.location.hostname.includes('localhost')
      ? `${window.location.origin}/login`
      : isHashMailboxHost
        ? 'https://mail.qixit.com/#/login'
        : 'https://mail.qixit.com/login';
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Username validation state
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState<UsernameAvailability | null>(null);
  
  // Email validation state
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailAvailability, setEmailAvailability] = useState<EmailAvailability | null>(null);
  
  const initialReferral = resolveReferralFromContext(searchParams);
  // Hide referral field when URL ref OR ad cookie is auto-applied (same UX as friend referral)
  const hasAppliedReferral = initialReferral.hasAppliedReferral;
  
  // Form data
  const [formData, setFormData] = useState<RegistrationRequest>({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    referralCode: initialReferral.effectiveRef,
    referralSource: initialReferral.effectiveSource,
    accountType: 'CONSUMER',
    acceptTerms: false,
    // Privacy acceptance remains true in payload for backend compatibility,
    // but the UI consent checkbox is intentionally removed per product decision.
    acceptPrivacy: true,
  });
  
  // Validation errors
  const [errors, setErrors] = useState<Partial<Record<keyof RegistrationRequest, string>>>({});

  // Debounced username check
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkUsername = useCallback(
    debounce(async (username: string, email?: string) => {
      if (username.length < 5) {
        setUsernameAvailability(null);
        return;
      }
      
      setCheckingUsername(true);
      try {
        const result = await registrationService.checkUsername(username, email);
        setUsernameAvailability(result);
      } catch (err) {
        console.error('Username check failed:', err);
      } finally {
        setCheckingUsername(false);
      }
    }, 500),
    []
  );

  // Debounced email check
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkEmail = useCallback(
    debounce(async (email: string) => {
      // Basic email format validation before checking
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setEmailAvailability(null);
        return;
      }
      
      setCheckingEmail(true);
      try {
        const result = await registrationService.checkEmail(email);
        setEmailAvailability(result);
      } catch (err) {
        console.error('Email check failed:', err);
      } finally {
        setCheckingEmail(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (formData.username) {
      checkUsername(formData.username, formData.email);
    }
  }, [formData.username, formData.email, checkUsername]);

  useEffect(() => {
    if (formData.email) {
      checkEmail(formData.email);
    }
  }, [formData.email, checkEmail]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Clear field error when user types
    if (errors[name as keyof RegistrationRequest]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const usernameRequirements =
    '5–50 characters; at least 3 letters and 2 numbers; letters, numbers, underscores, and hyphens only';

  const validateStep1 = (): boolean => {
    const newErrors: Partial<Record<keyof RegistrationRequest, string>> = {};
    
    if (!formData.username || formData.username.length < 5) {
      newErrors.username = 'Username must be at least 5 characters';
    } else if (formData.username.length > 50) {
      newErrors.username = 'Username cannot exceed 50 characters';
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, underscores, and hyphens';
    } else if (
      (formData.username.match(/[a-zA-Z]/g) || []).length < 3 ||
      (formData.username.match(/[0-9]/g) || []).length < 2
    ) {
      newErrors.username = 'Username must contain at least 3 letters and 2 numbers';
    } else if (usernameAvailability && !usernameAvailability.available) {
      newErrors.username = usernameAvailability.reason || 'Username not available';
    }
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (emailAvailability && !emailAvailability.available) {
      newErrors.email = emailAvailability.reason || 'Email not available';
    }
    
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/[a-zA-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one letter';
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Partial<Record<keyof RegistrationRequest, string>> = {};
    
    if (!formData.firstName || formData.firstName.trim().length === 0) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName || formData.lastName.trim().length === 0) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (formData.phoneNumber && !/^\+?[\d\s()-]{10,}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    const newErrors: Partial<Record<keyof RegistrationRequest, string>> = {};
    
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the Terms of Use';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    let valid = false;
    
    switch (activeStep) {
      case 0:
        valid = validateStep1();
        break;
      case 1:
        valid = validateStep2();
        break;
      case 2:
        valid = validateStep3();
        if (valid) {
          handleSubmit();
          return;
        }
        break;
    }
    
    if (valid) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await registrationService.register(formData);
      
      if (response.success) {
        clearStoredReferral();
        setSuccess(true);
        // Redirect to email verification page after 2 seconds
        setTimeout(() => {
          navigate('/verify-email', { 
            state: { 
              email: formData.email,
              userId: response.userId,
              username: response.username
            } 
          });
        }, 2000);
      } else {
        setError(response.message || 'Registration failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Create Your Account
      </Typography>
      
      <TextField
        fullWidth
        label="Username"
        name="username"
        value={formData.username}
        onChange={handleChange}
        error={!!errors.username || (usernameAvailability !== null && !usernameAvailability.available)}
        helperText={
          errors.username ||
          (usernameAvailability && !usernameAvailability.available
            ? usernameAvailability.reason
            : usernameAvailability?.available
              ? 'Username is available'
              : usernameRequirements)
        }
        margin="normal"
        inputProps={{ maxLength: 50 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Person />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {checkingUsername ? (
                <CircularProgress size={20} />
              ) : usernameAvailability?.available ? (
                <CheckCircle color="success" />
              ) : usernameAvailability ? (
                <Cancel color="error" />
              ) : null}
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        label="Email"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleChange}
        error={!!errors.email || (emailAvailability !== null && !emailAvailability.available)}
        helperText={errors.email || (emailAvailability && !emailAvailability.available ? emailAvailability.reason : '')}
        margin="normal"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email />
            </InputAdornment>
          ),
          endAdornment: formData.email && (
            <InputAdornment position="end">
              {checkingEmail ? (
                <CircularProgress size={20} />
              ) : emailAvailability ? (
                emailAvailability.available ? (
                  <CheckCircle color="success" />
                ) : (
                  <Cancel color="error" />
                )
              ) : null}
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        label="Password"
        name="password"
        type={showPassword ? 'text' : 'password'}
        value={formData.password}
        onChange={handleChange}
        error={!!errors.password}
        helperText={errors.password || 'At least 8 characters with letters and numbers'}
        margin="normal"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        label="Confirm Password"
        name="confirmPassword"
        type={showConfirmPassword ? 'text' : 'password'}
        value={formData.confirmPassword}
        onChange={handleChange}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword}
        margin="normal"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Lock />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
{/* Referral info is kept hidden to protect referrer identity */}
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Personal Information
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
            margin="normal"
          />
        </Grid>
      </Grid>
      
      <TextField
        fullWidth
        label="Phone Number (Optional)"
        name="phoneNumber"
        value={formData.phoneNumber}
        onChange={handleChange}
        error={!!errors.phoneNumber}
        helperText={errors.phoneNumber || 'Used for account recovery and verification'}
        margin="normal"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Phone />
            </InputAdornment>
          ),
        }}
      />
      
      {/* Only show referral code field if NOT pre-populated from URL or ad cookie */}
      {!hasAppliedReferral && (
        <TextField
          fullWidth
          label="Referral Code (Optional)"
          name="referralCode"
          value={formData.referralCode}
          onChange={handleChange}
          margin="normal"
          helperText="Enter a referral code if you were referred by someone"
        />
      )}
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review & Confirm
      </Typography>
      
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Username</Typography>
            <Typography variant="body1">{formData.username}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Email</Typography>
            <Typography variant="body1">{formData.email}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Name</Typography>
            <Typography variant="body1">{formData.firstName} {formData.lastName}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Phone</Typography>
            <Typography variant="body1">{formData.phoneNumber || 'Not provided'}</Typography>
          </Grid>
{/* Referral info hidden in review step to protect referrer identity */}
        </Grid>
      </Paper>
      
      <FormControlLabel
        control={
          <Checkbox
            name="acceptTerms"
            checked={formData.acceptTerms}
            onChange={handleChange}
          />
        }
        label={
          <Typography variant="body2">
            I agree to the{' '}
            <Link href="https://qixit.com/terms-of-use/" target="_blank" rel="noopener noreferrer">Terms of Use</Link>
          </Typography>
        }
      />
      {errors.acceptTerms && (
        <Typography color="error" variant="caption" display="block">
          {errors.acceptTerms}
        </Typography>
      )}
    </Box>
  );

  if (success) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Account Created Successfully!
          </Typography>
          <Typography color="text.secondary" paragraph>
            Please check your email to verify your account.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting to email verification...
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Create Account
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : activeStep === steps.length - 1 ? (
              'Create Account'
            ) : (
              'Next'
            )}
          </Button>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="body2" align="center">
          Already have an account?{' '}
          <Link
            component={isFramed ? 'a' : RouterLink}
            to={isFramed ? undefined : '/login'}
            href={isFramed ? mailboxLoginUrl : undefined}
            target={isFramed ? '_blank' : undefined}
            rel={isFramed ? 'noopener noreferrer' : undefined}
          >
            Sign in
          </Link>
        </Typography>
      </Paper>
    </Container>
  );
};

export default Register;
